import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    PayerInformation,
    PartnerLinkInformation,
    TransactionStatus,
    ExpiredQuoteData,
} from "@/helpers/interfaces/shared";
import { useSessionStore } from "./session";

/**
 * Checkout store to manage checkout-related data such as supplementary data,
 * query string data, payer information, partner link information, transaction
 * status, and validation triggers. The store persists data in sessionStorage
 * to maintain state across page reloads within the same session.
 */
export const useCheckoutStore = defineStore(
    "checkout",
    () => {
        // ── State ──────────────────────────────────────────────────────────────
        const supplementaryData = ref<any>(null);
        const queryStringData = ref<any>(null);
        const payerInformation = ref<PayerInformation | null>(null);
        const triggerPayerInformationValidation = ref(false);
        const triggerCardInformationValidation = ref(false);
        const partnerLinkInformation = ref<PartnerLinkInformation | null>(null);
        const partnerLinkAmount = ref<number | null>(null);
        const orderId = ref<string | null>(null);
        const overrideInvalidQueryStringData = ref<string[]>([]);
        const currentTransactionStatus = ref<TransactionStatus | null>(null);
        const expiredQuote = ref<ExpiredQuoteData | null>(null);
        const isExpiredQuote = computed(() => expiredQuote.value !== null);

        // ── Actions ────────────────────────────────────────────────────────────
        function setSupplementaryData(data: any) {
            /**
             * Sets the supplementary object collected from the user
             */
            supplementaryData.value = data;
        }

        function updateSupplementaryData(updatedData: any) {
            /**
             * Updates the supplementary data object with the values received.
             * If the supplementary data is initially null it sets it to an empty
             * object to avoid type errors. it loops each key in the new object and
             * checks if the key exist in the original one. if it does it then updates
             * the key's value with the one from the passed object.
             */
            if (supplementaryData.value === null) {
                supplementaryData.value = {};
            }

            const updatedObject = { ...supplementaryData.value };

            for (const key in updatedData) {
                if (Object.prototype.hasOwnProperty.call(updatedData, key)) {
                    /** Type assertion here since we know 'key' exists in 'updatedData' */
                    const updatedValue = (updatedData as any)[key];
                    (updatedObject as any)[key] = updatedValue;
                }
            }

            supplementaryData.value = updatedObject;
        }

        function setQueryStringData(data: any) {
            queryStringData.value = data;
        }

        /**
         * Clears specific keys from the query string data object, used when some of the prefilled data is invalid but we don't want to lose all prefilled data
         * @param {string[]} keys - the keys to be cleared from the query string data object
         */
        function clearInvalidQueryStringData(keys: string[]) {
            if (queryStringData.value === null) {
                return;
            }

            const updatedQueryStringData = { ...queryStringData.value };

            for (const key of keys) {
                if (
                    Object.prototype.hasOwnProperty.call(
                        updatedQueryStringData,
                        key,
                    )
                ) {
                    delete (updatedQueryStringData as any)[key];
                }
            }

            queryStringData.value = updatedQueryStringData;
        }

        function setPayerDetails(information: PayerInformation | null) {
            /**
             * Sets the address object to a given value
             */
            payerInformation.value = information;
        }

        function clearPayerAddress() {
            const sessionStore = useSessionStore();

            /**
             * Resets only the address fields within payerInformation, preserving
             * name fields. Used when the country changes because the stored address
             * is country-specific (postal code format, state requirement, countryCode)
             * and would be invalid or misleading for the newly selected country.
             */
            if (payerInformation.value === null) return;
            payerInformation.value = {
                ...payerInformation.value,
                address: {
                    addressLine1: "",
                    addressLine2: "",
                    addressLine3: "",
                    city: "",
                    state: "",
                    postalCode: "",
                    countryCode: sessionStore.countryCode || "GB",
                },
            };
        }

        function setTriggerPayerInformationValidation(value: boolean) {
            /**
             * Sets the boolean value for the payer information validation
             */
            triggerPayerInformationValidation.value = value;
        }

        function setTriggerCardInformationValidation(value: boolean) {
            /**
             * Sets the boolean value for the card information trigger
             */
            triggerCardInformationValidation.value = value;
        }

        function setPartnerLinkInformation(
            information: PartnerLinkInformation | null,
        ) {
            partnerLinkInformation.value = information;
        }

        function updatePartnerLinkInformation(updatedData: any) {
            /**
             * Updates the partner link data object with the values received.
             * If the partner link data is initially null it sets it to an empty
             * object to avoid type errors. it loops each key in the new object and
             * checks if the key exist in the original one. if it does it then updates
             * the key's value with the one from the passed object.
             */
            if (partnerLinkInformation.value === null) {
                partnerLinkInformation.value = {};
            }

            const updatedObject = { ...partnerLinkInformation.value };

            for (const key in updatedData) {
                if (Object.prototype.hasOwnProperty.call(updatedData, key)) {
                    /** Type assertion here since we know 'key' exists in 'updatedData' */
                    const updatedValue = (updatedData as any)[key];
                    (updatedObject as any)[key] = updatedValue;
                }
            }

            partnerLinkInformation.value = updatedObject;
        }

        function setPartnerLinkAmount(amount: number | null) {
            partnerLinkAmount.value = amount;
        }

        function setOrderId(id: string | null) {
            orderId.value = id;
        }

        /**
         * Sets the list of keys with invalid prefilled data in the query string, used to determine which fields
         * should have their prefilled data cleared when the supplementary form is submitted with invalid prefilled data
         * @param {string[]} override
         */
        function setOverrideInvalidQueryStringData(override: string[]) {
            overrideInvalidQueryStringData.value = override;
        }

        /**
         * Adds a key to the list of keys with invalid prefilled data in the query string, used to determine which fields
         * should have their prefilled data cleared when the supplementary form is submitted with invalid prefilled data
         * @param {string} key - the key to be added to the list
         */
        function addOverrideInvalidQueryStringDataKey(key: string) {
            if (
                !(overrideInvalidQueryStringData.value as string[]).includes(
                    key,
                )
            ) {
                (overrideInvalidQueryStringData.value as string[]).push(key);
            }
        }

        function setCurrentTransactionStatus(
            transactionStatus: TransactionStatus | null,
        ) {
            currentTransactionStatus.value = transactionStatus;
        }

        function setExpiredQuote(data: ExpiredQuoteData | null) {
            expiredQuote.value = data;
        }

        return {
            supplementaryData,
            queryStringData,
            payerInformation,
            triggerPayerInformationValidation,
            triggerCardInformationValidation,
            partnerLinkInformation,
            partnerLinkAmount,
            orderId,
            overrideInvalidQueryStringData,
            currentTransactionStatus,
            expiredQuote,
            isExpiredQuote,
            setSupplementaryData,
            updateSupplementaryData,
            setQueryStringData,
            clearInvalidQueryStringData,
            setPayerDetails,
            clearPayerAddress,
            setTriggerPayerInformationValidation,
            setTriggerCardInformationValidation,
            setPartnerLinkInformation,
            updatePartnerLinkInformation,
            setPartnerLinkAmount,
            setOrderId,
            setOverrideInvalidQueryStringData,
            addOverrideInvalidQueryStringDataKey,
            setCurrentTransactionStatus,
            setExpiredQuote,
        };
    },
    {
        persist: {
            pick: [
                "supplementaryData",
                "queryStringData",
                "payerInformation",
                "triggerPayerInformationValidation",
                "triggerCardInformationValidation",
                "partnerLinkInformation",
                "partnerLinkAmount",
                "orderId",
                "overrideInvalidQueryStringData",
            ],
            storage: sessionStorage,
        },
    },
);
